// Management → CW Squad — live board + monthly BRB / inactivity reports (CSV).
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Coffee, Download, Loader2, Moon, Radio } from "lucide-react";
import { Kpis, PeopleTable, RatioPanel, TeamChart, METRICS } from "./squad/SquadStats";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { useSquad } from "../../components/squad/SquadContext";
import SquadAvatar, { STATUS } from "../../components/squad/SquadAvatar";
import { dur } from "../../components/squad/squadUtils";

function LiveBoard() {
  const { squad } = useSquad() || {};
  const c = squad?.counts || {};
  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid="squad-live-board">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base md:text-lg font-semibold text-white">Team live status</h2>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono text-emerald-300 bg-emerald-500/10 border border-emerald-400/30"><Radio className="w-3 h-3" /> Live</span>
      </div>
      <div className="grid grid-cols-3 mb-5">
        {Object.entries(STATUS).map(([k, s]) => (
          <div key={k}><div className={`text-2xl font-bold tabular-nums ${s.text}`} data-testid={`squad-board-count-${k}`}>{c[k] || 0}</div><div className="text-[11px] text-slate-500">{s.label}</div></div>
        ))}
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-8 gap-4">
        {(squad?.members || []).map((m) => (
          <div key={m.id} className="flex flex-col items-center gap-1 min-w-0" data-testid={`squad-board-member-${m.id}`}>
            <SquadAvatar name={m.name} url={m.avatar_url} status={m.status} size={48} />
            <span className="text-[12px] text-white truncate max-w-full">{m.name}</span>
            <span className={`text-[10.5px] ${STATUS[m.status].text}`}>{STATUS[m.status].label}{m.reason ? ` · ${m.reason}` : ""}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

const FILE = { brb: "brb-breaks", auto: "auto-away", daily: "daily" };

function DownloadBtn({ kind, month, label = "Download CSV" }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/squad/reports/${kind}.csv`, { params: { month }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data);
      a.download = `cw-squad-${FILE[kind]}-${month}.csv`;
      a.click(); URL.revokeObjectURL(a.href);
    } catch { toast.error("Download failed"); } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={download} disabled={busy} data-testid={`squad-download-${kind}`}
      className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-white text-[#0b1220] text-[12.5px] font-semibold hover:bg-orange-100 transition-colors whitespace-nowrap">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} {label}
    </button>
  );
}

function ReportCard({ kind, title, sub, Icon, rows, month }) {
  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid={`squad-report-${kind}`}>
      <div className="flex items-start gap-3 mb-3">
        <Icon className="w-5 h-5 text-orange-300 mt-0.5" />
        <div className="flex-1"><h2 className="text-base md:text-lg font-semibold text-white">{title}</h2><p className="text-[12px] text-slate-400">{sub}</p></div>
        <DownloadBtn kind={kind} month={month} />
      </div>
      <table className="w-full text-[13px]">
        <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-white/[0.06]">
          <th className="py-2 font-medium">Staff</th><th className="font-medium">{kind === "brb" ? "Breaks" : "Times"}</th><th className="font-medium">Time</th><th className="font-medium">Longest</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.user_id} className="border-b border-white/[0.04]" data-testid={`squad-${kind}-row-${r.user_id}`}>
              <td className="py-2 text-white">{r.name}</td><td className="tabular-nums">{r.count}</td><td className="tabular-nums">{dur(r.total_s)}</td><td className="tabular-nums">{dur(r.longest_s)}</td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={5} className="py-6 text-center text-slate-500" data-testid={`squad-${kind}-empty`}>Nothing recorded this month.</td></tr>}
        </tbody>
      </table>
    </section>
  );
}

export default function SquadReports() {
  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [person, setPerson] = useState(null);
  const [metrics, setMetrics] = useState(METRICS.map((m) => m.key));
  useEffect(() => {
    api.get("/squad/reports/months").then(({ data: d }) => { setMonths(d.months); setMonth(d.months[0]); })
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load reports"));
  }, []);
  const load = useCallback(() => { if (month) api.get("/squad/reports", { params: { month } }).then(({ data: d }) => setData(d)).catch(() => setErr("Could not load reports")); }, [month]);
  useEffect(() => { load(); }, [load]);
  const label = (m) => new Date(`${m}-01T12:00:00`).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  return (
    <Layout>
      <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-6 space-y-5" data-testid="squad-reports-page">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-mono text-fuchsia-300/80">Management</div>
            <h1 className="text-2xl font-display font-bold text-white">CW Squad</h1>
            <p className="text-[12px] text-slate-400">Live availability, online time, BRB breaks and auto-away, month by month.</p>
          </div>
          {months.length > 0 && month && <span className="ml-auto"><DownloadBtn kind="daily" month={month} label="Daily breakdown CSV" /></span>}
          {months.length > 0 && (
            <select value={month || ""} onChange={(e) => setMonth(e.target.value)} data-testid="squad-month-select"
              className="h-9 px-3 rounded-lg bg-[#0F1A30] border border-white/10 text-[13px] text-slate-200">
              {months.map((m) => <option key={m} value={m}>{label(m)}</option>)}
            </select>
          )}
        </div>
        {err && <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 text-rose-200 px-4 py-3 text-sm" data-testid="squad-reports-error">{err}</div>}
        {!err && <LiveBoard />}
        {data && (
          <>
            <Kpis t={data.totals} />
            <div className="grid grid-cols-1 xl:grid-cols-[1.6fr_1fr] gap-5 [&>*]:min-w-0">
              <TeamChart daily={data.team_daily} people={data.people} month={month} person={person} setPerson={setPerson} metrics={metrics} setMetrics={setMetrics} />
              <RatioPanel people={data.people} totals={data.totals} person={person} setPerson={setPerson} />
            </div>
            <PeopleTable people={data.people} person={person} setPerson={setPerson} />
            <div className="grid lg:grid-cols-2 gap-5">
              <ReportCard kind="brb" title="Report A — BRB / Breaks" sub={`${data.totals.brb_count} breaks · ${dur(data.totals.brb_s)} total`} Icon={Coffee} rows={data.brb} month={month} />
              <ReportCard kind="auto" title="Report B — Auto away (20 min quiet)" sub={`${data.totals.auto_count} times · ${dur(data.totals.auto_s)} total`} Icon={Moon} rows={data.auto} month={month} />
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
